'use client';

import dynamic from 'next/dynamic';
import { type ReactNode, useMemo } from 'react';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import CommunityActivity from '@alltrails/maps/components/CommunityActivity/CommunityActivity';
import Photospots from '@alltrails/maps/components/Photospots/Photospots';
import type { HeatmapLayerStyle } from '@alltrails/maps/components/PolylineHeatmapLayer/PolylineHeatmapLayer';
import CommunityWaypoints from '@alltrails/maps/components/Waypoints/CommunityWaypoints';
import UserWaypoints from '@alltrails/maps/components/Waypoints/UserWaypoints';
import useIsPage from '@/hooks/useIsPage';
import { useAppSelector } from '@/utils/redux';

const HighDefTerrainOverlay = dynamic(() => import('@alltrails/advanced-map-layers/components/HighDefTerrainOverlay/HighDefTerrainOverlay'), {
  ssr: false
});
const PhotoHeatmapOverlay = dynamic(() => import('@alltrails/advanced-map-layers/components/PhotoHeatmapOverlay/PhotoHeatmapOverlay'), {
  ssr: false
});
const PrivateLandsOverlay = dynamic(() => import('@alltrails/advanced-map-layers/components/PrivateLandsOverlay/PrivateLandsOverlay'), {
  ssr: false
});
const PublicLandsOverlay = dynamic(() => import('@alltrails/advanced-map-layers/components/PublicLandsOverlay/PublicLandsOverlay'), { ssr: false });
const RecordingEndOverlay = dynamic(() => import('@alltrails/advanced-map-layers/components/RecordingEndOverlay/RecordingEndOverlay'), {
  ssr: false
});
const FallFoliageOverlay = dynamic(() => import('@alltrails/advanced-map-layers/components/FallFoliageOverlay/FallFoliageOverlay'), {
  ssr: false
});
const RecordingStartOverlay = dynamic(() => import('@alltrails/advanced-map-layers/components/RecordingStartOverlay/RecordingStartOverlay'), {
  ssr: false
});
const SlopeAngleOverlay = dynamic(() => import('@alltrails/advanced-map-layers/components/SlopeAngleOverlay/SlopeAngleOverlay'), { ssr: false });
const WildfireOverlay = dynamic(() => import('@alltrails/advanced-map-layers/components/WildfireOverlay/WildfireOverlay'), { ssr: false });
const ConditionOverlays = dynamic(() => import('@alltrails/conditions/components/ConditionOverlays'), { ssr: false });
const CommunityHeatmap = dynamic(() => import('@alltrails/heatmaps/components/CommunityHeatmap'), { ssr: false });
const PersonalHeatmap = dynamic(() => import('@alltrails/heatmaps/components/PersonalHeatmap'), { ssr: false });
const TrailHeatmap = dynamic(() => import('@alltrails/heatmaps/components/TrailHeatmap'), { ssr: false });

type ExploreMapOverlaysProps = {
  currentLocationMarker?: ReactNode;
  disablePopups?: boolean;
  heatmapStyle?: HeatmapLayerStyle;
  showConditionOverlays?: boolean;
};

const ExploreMapOverlays = ({ currentLocationMarker, disablePopups, heatmapStyle, showConditionOverlays }: ExploreMapOverlaysProps) => {
  const experiments = useExperiments();
  const isPhotoHeatmapEnabled = experiments?.['web-tmt-rm-photos-overlay']?.value === 'on';
  const isRecordingOverlaysEnabled = experiments?.['web-tmt-rm-recording-overlays']?.value === 'on';
  const isFallFoliageEnabled = experiments?.['web-disco-rm-fall-foliage']?.value === 'on';
  const { isCustomRoute, isMembersCustomRoute } = useIsPage();
  const dynamicStyleIds = useAppSelector(state => state.map.dynamicStyleIds);
  const customRouteId = useAppSelector(state => state.customRoute.route.id);
  const trailId = useAppSelector(state => state.explore.currentTrail?.id);

  const photoHeatmapIsApplied = useMemo(
    () => isPhotoHeatmapEnabled && dynamicStyleIds.includes('photoHeatmap'),
    [isPhotoHeatmapEnabled, dynamicStyleIds]
  );
  const recordingStartIsApplied = useMemo(
    () => isRecordingOverlaysEnabled && dynamicStyleIds.includes('recordingStart'),
    [isRecordingOverlaysEnabled, dynamicStyleIds]
  );
  const recordingEndIsApplied = useMemo(
    () => isRecordingOverlaysEnabled && dynamicStyleIds.includes('recordingEnd'),
    [isRecordingOverlaysEnabled, dynamicStyleIds]
  );
  const fallFoliageIsApplied = useMemo(
    () => isFallFoliageEnabled && dynamicStyleIds.includes('fallFoliage'),
    [isFallFoliageEnabled, dynamicStyleIds]
  );

  return (
    <>
      {showConditionOverlays && (
        <ConditionOverlays customRouteId={isCustomRoute || isMembersCustomRoute ? customRouteId : undefined} trailId={trailId} />
      )}
      {dynamicStyleIds?.includes('newHeatmap') && <CommunityHeatmap />}
      {dynamicStyleIds?.includes('trailHeatmap') && <TrailHeatmap />}
      {dynamicStyleIds?.includes('publicLands') && <PublicLandsOverlay disablePopups={disablePopups} />}
      {dynamicStyleIds?.includes('wildfire') && <WildfireOverlay disablePopups={disablePopups} />}
      {fallFoliageIsApplied && <FallFoliageOverlay />}
      {dynamicStyleIds?.includes('highDefTerrain') && <HighDefTerrainOverlay />}
      {photoHeatmapIsApplied && <PhotoHeatmapOverlay />}
      {recordingStartIsApplied && <RecordingStartOverlay />}
      {recordingEndIsApplied && <RecordingEndOverlay />}
      <PersonalHeatmap style={heatmapStyle} />
      {dynamicStyleIds?.includes('privateLands') && <PrivateLandsOverlay disablePopups={disablePopups} />}
      {dynamicStyleIds?.includes('slopeAngle') && <SlopeAngleOverlay />}
      {dynamicStyleIds?.includes('communityActivity') && <CommunityActivity />}
      {currentLocationMarker}
      {dynamicStyleIds?.includes('photospot') && <Photospots />}
      {dynamicStyleIds?.includes('userWaypoints') && <UserWaypoints />}
      {dynamicStyleIds?.includes('communityWaypoints') && <CommunityWaypoints />}
    </>
  );
};

export default ExploreMapOverlays;
